import {
  CloseIcon,
  getStorageWithExpiry,
  setStorageWithExpiry,
} from "@brandclub/common-ui";

import { styled, keyframes, useTheme } from "@mui/material";
import { useState } from "react";
import { getMobileOperatingSystem } from "../../utils";
import { formatMoney } from "../../utils/string";
import {
  useDomainConfig,
  useBrandAppConfigWithFallback,
} from "@/api/cache/appConfigQueryOptions";
import { useBrandclubJoinReward } from "@/api/cache/brandclubForBrandPublicQueryOptions";
import { AppSettings } from "@brandclub/common-ui/theme/types";

const GET_APP_BANNER_STORAGE_KEY = "bc_get_app_custom_banner_hidden";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Container = styled("div")(({ theme }) => ({
  position: "fixed",
  zIndex: 1000,

  width: "min(100% - 40px, 430px)",
  minWidth: 352,
  maxWidth: 430,
  height: 90,
  boxSizing: "border-box",
  backgroundColor: theme.vars.palette.background.default,
  borderRadius: 16,
  boxShadow: "0 3px 6px 0 rgba(31, 27, 27, 0.1)",
  padding: 16,
  display: "flex",
  gap: "15px",
  left: "50%",
  transform: "translate(-50%, 0)",
  alignItems: "center",

  animation: `${fadeIn} 0.3s ease-in`,
  ".bc-get-app-banner-logo": {
    width: 64,
    height: 64,
    borderRadius: 11,
    objectFit: "cover",
    boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.16)",
  },
  ".bc-get-app-banner-text": {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
  },
  ".bc-get-app-banner-title": {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "12px",
    color: theme.vars.palette.text.primary,
  },
  ".bc-get-app-banner-description": {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "12px",
    color: theme.vars.palette.text.primary,
  },
  ".bc-get-app-banner-operation": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.vars.palette.primary.main,
    borderRadius: 999,
    padding: "8px 16.5px",
    ".bc-get-app-banner-operation-button-text": {
      color: theme.vars.palette.primary.contrastText,
      fontSize: 14,
      fontWeight: 700,
    },
  },
  ".bc_get_app_banner_close_button": {
    outline: "none",
    border: "none",
    width: "28px !important",
    height: "28px !important",
    maxWidth: "28px !important",
    maxHeight: "28px !important",
    minWidth: "28px !important",
    minHeight: "28px !important",

    position: "absolute",
    right: 0,
    top: -14,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f6f7f8",
    cursor: "pointer",
  },
}));

const getCustomAppBannerSettings = (
  themeAppSettings: AppSettings | undefined,
  mobileOperatingSystem: "Android" | "iOS"
) => {
  if (!themeAppSettings) {
    return undefined;
  }
  const smartBannerEnabled = themeAppSettings.smartBannerType === "both";
  if (mobileOperatingSystem === "iOS") {
    return {
      enabled: themeAppSettings.smartBannerType === "ios" || smartBannerEnabled,
      alignment: themeAppSettings.iosCustomBannerAlignment,
    };
  }
  return {
    enabled:
      themeAppSettings.smartBannerType === "android" || smartBannerEnabled,
    alignment: themeAppSettings.androidCustomBannerAlignment,
  };
};

const CustomGetAppBanners = () => {
  const [isOpen, setIsOpen] = useState(
    getStorageWithExpiry(GET_APP_BANNER_STORAGE_KEY) !== "hidden"
  );
  const theme = useTheme();
  const { data: domainConfig } = useDomainConfig();
  const brandId = domainConfig?.brandId;
  const { data: brandAppConfig } = useBrandAppConfigWithFallback();

  const { data: joinBrandclubReward = 0, isLoading } =
    useBrandclubJoinReward(brandId);

  const mobileOperatingSystem = getMobileOperatingSystem();

  const hasIOSApp = !!brandAppConfig?.appStoreUrl;
  const hasAndroidApp = !!brandAppConfig?.playStoreUrl;

  const appImage = brandAppConfig?.mobileAppConfig?.appIcon;
  const appLink = brandAppConfig?.branchIOConfig?.mobileAppBranchLink;

  if (isLoading) {
    return null;
  }

  if (!hasIOSApp && mobileOperatingSystem === "iOS") {
    return null;
  }

  if (!hasAndroidApp && mobileOperatingSystem === "Android") {
    return null;
  }

  if (mobileOperatingSystem === "unknown") {
    return null;
  }

  const customAppBannerSettings = getCustomAppBannerSettings(
    theme.settings?.app,
    mobileOperatingSystem
  );

  if (!customAppBannerSettings?.enabled || !appImage || !appLink || !isOpen) {
    return null;
  }
  const { enabled, alignment } = customAppBannerSettings;
  const onGetApp = () => {
    window.open(appLink, "_blank");
  };

  const handleClose = () => {
    setIsOpen(false);
    setStorageWithExpiry(GET_APP_BANNER_STORAGE_KEY, "hidden", 60 * 60 * 24);
  };

  return (
    <Container
      sx={{
        bottom: alignment?.align === "bottom" ? alignment?.alignOffset : "auto",
        top: alignment?.align === "top" ? alignment?.alignOffset : "auto",
      }}
    >
      <button
        className="bc_get_app_banner_close_button"
        onClick={handleClose}
        aria-label="Close"
      >
        <CloseIcon
          sx={(theme) => ({
            width: 12,
            height: 12,
            color: theme.vars.palette.text.primary,
          })}
        ></CloseIcon>
      </button>
      <img
        className="bc-get-app-banner-logo"
        alt="bc-get-app-logo"
        src={appImage}
      ></img>
      <div className="bc-get-app-banner-text">
        <div className="bc-get-app-banner-title">PetArmor</div>
        <div className="bc-get-app-banner-description">
          {joinBrandclubReward > 0
            ? `Earn ${formatMoney(
                joinBrandclubReward,
                undefined,
                false
              )} when you join`
            : "Earn exclusive rewards"}
        </div>
      </div>
      <div className="bc-get-app-banner-operation">
        <div
          className="bc-get-app-banner-operation-button-text"
          onClick={onGetApp}
        >
          Get App
        </div>
      </div>
    </Container>
  );
};

export default CustomGetAppBanners;
